import { useState, useEffect } from "react";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import {
  Users, Package, Calendar, DollarSign, TrendingUp, Settings,
  Hotel, Bus, Car, Bell, CheckCircle2, XCircle, Eye, FileText, Plus, Pencil, Trash2, UserCheck, Download, Receipt, Sun, Moon, RefreshCw
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import DestinationsTab, { DestinationsWidget } from "./DestinationsTab";
import ReviewsModerationTab from "./ReviewsModerationTab";
import CompaniesTab from "./CompaniesTab";
import AffiliatesTab from "./AffiliatesTab";
import PendingListingsTab from "./PendingListingsTab";
import MigrationStatusTab from "./MigrationStatusTab";
import RedisStatsTab from "./RedisStatsTab";
import TopAttackersWidget from "../components/TopAttackersWidget";
import SystemHealthStrip from "../components/SystemHealthStrip";
import AdminProfileTab from "./AdminProfileTab";
import DocVerificationTab from "./DocVerificationTab";
import DeletionsTab from "./DeletionsTab";

function StatCard({ icon: Icon, label, value, color = "#2C5F2D", testId }) {
  return (
    <div className="bg-white border border-[#E5E3DB] rounded-xl p-5" data-testid={testId}>
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center`} style={{ backgroundColor: `${color}15` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div>
          <p className="text-xs text-[#8A998E] font-semibold uppercase tracking-wider">{label}</p>
          <p className="text-xl font-bold text-[#1A231C] font-['Outfit']">{value}</p>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [listings, setListings] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [settings, setSettings] = useState(null);
  const [commissionForm, setCommissionForm] = useState({ hotel: 10, bus: 8, cab: 12 });
  const [loading, setLoading] = useState(true);
  const [blogs, setBlogs] = useState([]);
  const [pendingHosts, setPendingHosts] = useState([]);
  const [selectedHostIds, setSelectedHostIds] = useState(new Set());
  const [showBlogForm, setShowBlogForm] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [blogForm, setBlogForm] = useState({ title: "", content: "", excerpt: "", image: "", published: true });
  const [theme, setTheme] = useState(() => (typeof document !== "undefined" ? document.documentElement.getAttribute("data-mode") || "admin" : "admin"));
  const toggleTheme = () => {
    const next = theme === "admin" ? "light" : "admin";
    document.documentElement.setAttribute("data-mode", next);
    localStorage.setItem("ss_admin_theme", next);
    setTheme(next);
  };
  const [gstMonth, setGstMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [gstData, setGstData] = useState(null);
  const [gstLoading, setGstLoading] = useState(false);

  const loadGstInvoices = async (month) => {
    setGstLoading(true);
    try {
      const res = await api.get(`/admin/invoices?month=${month}`);
      setGstData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load invoices");
      setGstData({ items: [], summary: {} });
    } finally {
      setGstLoading(false);
    }
  };

  const downloadGstrCsv = async (month) => {
    try {
      const res = await api.get(`/admin/gstr1-export?month=${month}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `gstr1_${month}.csv`; document.body.appendChild(a); a.click();
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 200);
    } catch (e) { toast.error("Failed to download GSTR-1 CSV"); }
  };

  const downloadAdminInvoice = async (bookingId, invoiceNumber) => {
    try {
      const res = await api.get(`/bookings/${bookingId}/invoice.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(invoiceNumber || "invoice").replace(/\//g, "_")}.pdf`; document.body.appendChild(a); a.click();
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 200);
    } catch (e) { toast.error("Failed to download invoice"); }
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, bookingsRes, listingsRes, settingsRes, notifsRes, blogsRes, pendingRes] = await Promise.all([
        api.get("/admin/dashboard"),
        api.get("/admin/users"),
        api.get("/admin/bookings"),
        api.get("/admin/listings"),
        api.get("/admin/settings"),
        api.get("/admin/notifications"),
        api.get("/blog?published_only=false"),
        api.get("/admin/pending-hosts")
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setBookings(bookingsRes.data.bookings);
      setListings(listingsRes.data.listings);
      setSettings(settingsRes.data);
      setNotifications(notifsRes.data.notifications);
      setBlogs(blogsRes.data.blogs);
      setPendingHosts(pendingRes.data.hosts || []);
      if (settingsRes.data.commission_rates) {
        setCommissionForm(settingsRes.data.commission_rates);
      }
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const updateCommission = async () => {
    try {
      await api.put("/admin/commission", commissionForm);
      toast.success("Commission rates updated");
      fetchAll();
    } catch { toast.error("Failed to update"); }
  };

  const toggleListingStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    try {
      await api.put(`/admin/listings/${id}/status?status=${newStatus}`);
      toast.success(`Listing ${newStatus}`);
      fetchAll();
    } catch { toast.error("Failed to update"); }
  };

  const changeListingType = async (id, newType, currentType) => {
    if (!newType || newType === currentType) return;
    try {
      await api.put(`/admin/listings/${id}/type`, { type: newType });
      toast.success(`Type changed: ${currentType} → ${newType}`);
      setListings(prev => prev.map(l => l.id === id ? { ...l, type: newType } : l));
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to change type"); }
  };

  const backfillListingTypes = async () => {
    if (!window.confirm("Sync every stay listing's type to match its host's partner_meta.property_type? Safe + reversible per-row via the dropdown.")) return;
    try {
      const r = await api.post("/admin/listings/backfill-types-from-partner-meta");
      toast.success(`Backfill complete: ${r.data.changed} changed · ${r.data.scanned} scanned · ${r.data.skipped} skipped`);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Backfill failed"); }
  };

  const handleBlogSubmit = async () => {
    try {
      if (editingBlog) {
        await api.put(`/blog/${editingBlog.id}`, blogForm);
        toast.success("Blog updated");
      } else {
        await api.post("/blog", blogForm);
        toast.success("Blog published");
      }
      setShowBlogForm(false); setEditingBlog(null);
      setBlogForm({ title: "", content: "", excerpt: "", image: "", published: true });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const deleteBlog = async (id) => {
    if (!window.confirm("Delete this blog post?")) return;
    try { await api.delete(`/blog/${id}`); toast.success("Blog deleted"); fetchAll(); }
    catch { toast.error("Failed"); }
  };

  const actOnHost = async (userId, action) => {
    const msg = action === "approve"
      ? "Approve this partner and send them an approval email?"
      : "Reject this partner application?";
    if (!window.confirm(msg)) return;
    try {
      await api.post(`/admin/approve-host/${userId}?action=${action}`);
      toast.success(`Partner ${action}d successfully`);
      setSelectedHostIds(prev => { const n = new Set(prev); n.delete(userId); return n; });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const toggleHostSelection = (id) => {
    setSelectedHostIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllHosts = () => {
    if (selectedHostIds.size === pendingHosts.length) setSelectedHostIds(new Set());
    else setSelectedHostIds(new Set(pendingHosts.map(h => h.id)));
  };

  const bulkActOnHosts = async (action) => {
    if (selectedHostIds.size === 0) { toast.error("No partners selected"); return; }
    const ids = Array.from(selectedHostIds);
    const msg = action === "approve"
      ? `Approve ${ids.length} partner${ids.length > 1 ? "s" : ""} and send approval emails?`
      : `Reject ${ids.length} partner application${ids.length > 1 ? "s" : ""}?`;
    if (!window.confirm(msg)) return;
    try {
      const res = await api.post("/admin/bulk-approve-hosts", { user_ids: ids, action });
      const { updated, emailed } = res.data;
      toast.success(`${action === "approve" ? "Approved" : "Rejected"} ${updated} partner${updated === 1 ? "" : "s"} · ${emailed} email${emailed === 1 ? "" : "s"} sent`);
      setSelectedHostIds(new Set());
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk action failed");
    }
  };

  const statusColor = (s) => {
    if (s === "confirmed" || s === "active" || s === "sent") return "bg-green-100 text-green-700";
    if (s === "pending" || s === "mock_sent") return "bg-amber-100 text-amber-700";
    if (s === "cancelled" || s === "inactive" || s === "failed") return "bg-red-100 text-red-700";
    return "bg-gray-100 text-gray-600";
  };

  if (loading) return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[1,2,3,4].map(i => <div key={i} className="h-24 bg-white rounded-xl border border-[#E5E3DB] animate-pulse" />)}
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-dashboard">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1A231C] font-['Outfit']">Admin Dashboard</h1>
          <p className="text-sm text-[#5C6B60] mt-1">Platform overview &amp; management</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="rounded-full border-[#E5E3DB] hover:border-[#2C5F2D] gap-1.5"
          onClick={toggleTheme}
          aria-label="Toggle theme"
          data-testid="admin-theme-toggle"
          title={theme === "admin" ? "Switch to Light theme" : "Switch to Dark theme"}
        >
          {theme === "admin" ? <><Sun className="w-4 h-4" /> Light</> : <><Moon className="w-4 h-4" /> Dark</>}
        </Button>
      </div>

      <SystemHealthStrip />

      {/* Stats Grid */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Users} label="Total Users" value={stats.total_users} testId="stat-users" />
          <StatCard icon={Package} label="Total Listings" value={stats.total_listings} color="#E07A5F" testId="stat-listings" />
          <StatCard icon={Calendar} label="Bookings" value={stats.total_bookings} testId="stat-bookings" />
          <StatCard icon={TrendingUp} label="Commission Earned" value={`INR ${stats.total_commission?.toLocaleString()}`} color="#2C5F2D" testId="stat-commission" />
        </div>
      )}

      {/* Destinations widget */}
      <DestinationsWidget />

      {/* Security: live attackers in last 24h (renders 'all clear' pill when empty) */}
      <TopAttackersWidget />

      <Tabs defaultValue={pendingHosts.length > 0 ? "approvals" : "bookings"}>
        <TabsList className="bg-[#F3F2EE] rounded-full mb-6 h-10 w-auto inline-flex flex-wrap">
          <TabsTrigger value="approvals" className="rounded-full data-[state=active]:bg-[#2C5F2D] data-[state=active]:text-white text-sm px-3" data-testid="admin-approvals-tab">
            Partner Approvals{pendingHosts.length > 0 && <span className="ml-1.5 bg-amber-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">{pendingHosts.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="bookings" className="rounded-full data-[state=active]:bg-[#2C5F2D] data-[state=active]:text-white text-sm px-3" data-testid="admin-bookings-tab">Bookings</TabsTrigger>
          <TabsTrigger value="listings" className="rounded-full data-[state=active]:bg-[#2C5F2D] data-[state=active]:text-white text-sm px-3" data-testid="admin-listings-tab">Listings</TabsTrigger>
          <TabsTrigger value="users" className="rounded-full data-[state=active]:bg-[#2C5F2D] data-[state=active]:text-white text-sm px-3" data-testid="admin-users-tab">Users</TabsTrigger>
          <TabsTrigger value="notifications" className="rounded-full data-[state=active]:bg-[#2C5F2D] data-[state=active]:text-white text-sm px-3" data-testid="admin-notifications-tab">Notifications</TabsTrigger>
          <TabsTrigger value="settings" className="rounded-full data-[state=active]:bg-[#2C5F2D] data-[state=active]:text-white text-sm px-3" data-testid="admin-settings-tab">Settings</TabsTrigger>
          <TabsTrigger value="gst" className="rounded-full data-[state=active]:bg-[#2C5F2D] data-[state=active]:text-white text-sm px-3" data-testid="admin-gst-tab" onClick={() => loadGstInvoices(gstMonth)}>GST Reports</TabsTrigger>
          <TabsTrigger value="blog" className="rounded-full data-[state=active]:bg-[#2C5F2D] data-[state=active]:text-white text-sm px-3" data-testid="admin-blog-tab">Blog ({blogs.length})</TabsTrigger>
          <TabsTrigger value="destinations" className="rounded-full data-[state=active]:bg-[#2C5F2D] data-[state=active]:text-white text-sm px-3" data-testid="admin-destinations-tab">Destinations</TabsTrigger>
          <TabsTrigger value="reviews" className="rounded-full data-[state=active]:bg-[#2C5F2D] data-[state=active]:text-white text-sm px-3" data-testid="admin-reviews-tab">Reviews</TabsTrigger>
          <TabsTrigger value="companies" className="rounded-full data-[state=active]:bg-[#2C5F2D] data-[state=active]:text-white text-sm px-3" data-testid="admin-companies-tab">B2B</TabsTrigger>
          <TabsTrigger value="affiliates" className="rounded-full data-[state=active]:bg-[#2C5F2D] data-[state=active]:text-white text-sm px-3" data-testid="admin-affiliates-tab">Affiliates</TabsTrigger>
          <TabsTrigger value="pending-listings" className="rounded-full data-[state=active]:bg-[#2C5F2D] data-[state=active]:text-white text-sm px-3" data-testid="admin-pending-listings-tab">Pending Listings</TabsTrigger>
          <TabsTrigger value="migration" className="rounded-full data-[state=active]:bg-[#2C5F2D] data-[state=active]:text-white text-sm px-3" data-testid="admin-migration-tab">DB Migration</TabsTrigger>
          <TabsTrigger value="redis" className="rounded-full data-[state=active]:bg-[#2C5F2D] data-[state=active]:text-white text-sm px-3" data-testid="admin-redis-tab">Redis</TabsTrigger>
          <TabsTrigger value="doc-verify" className="rounded-full data-[state=active]:bg-[#2C5F2D] data-[state=active]:text-white text-sm px-3" data-testid="admin-doc-verify-tab">Doc Verify</TabsTrigger>
          <TabsTrigger value="deletions" className="rounded-full data-[state=active]:bg-[#2C5F2D] data-[state=active]:text-white text-sm px-3" data-testid="admin-deletions-tab">Deletions</TabsTrigger>
          <TabsTrigger value="profile" className="rounded-full data-[state=active]:bg-[#2C5F2D] data-[state=active]:text-white text-sm px-3" data-testid="admin-profile-tab">Profile</TabsTrigger>
        </TabsList>

        {/* Partner Approvals Tab */}
        <TabsContent value="approvals">
          <div className="space-y-3">
            <div className="bg-[#FEF3C7] border border-[#FDE68A] rounded-xl p-4 flex items-start gap-3">
              <UserCheck className="w-5 h-5 text-[#B45309] mt-0.5 flex-shrink-0" />
              <div className="text-sm text-[#78350F]">
                <p className="font-semibold">Partner onboarding flow</p>
                <p className="text-xs mt-1 text-[#92400E]">New partners register on safarsetu.com/partner and remain in <strong>pending</strong> status. Approve them here to unlock their Extranet portal (extranet.safarsetu.com). An email notification is automatically sent from no-reply@safarsetu.com.</p>
              </div>
            </div>
            {pendingHosts.length === 0 ? (
              <div className="text-center py-12 text-[#8A998E] bg-white rounded-xl border border-[#E5E3DB]">
                <UserCheck className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p className="font-medium">No pending partner applications</p>
                <p className="text-xs mt-1">All caught up! New applications will appear here.</p>
              </div>
            ) : (<>
              <div className="bg-white rounded-xl border border-[#E5E3DB] p-3 flex items-center justify-between gap-3 flex-wrap" data-testid="bulk-toolbar">
                <label className="flex items-center gap-2 text-sm font-medium text-[#1A231C] cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 accent-[#2C5F2D]" checked={selectedHostIds.size === pendingHosts.length && pendingHosts.length > 0} onChange={toggleAllHosts} data-testid="select-all-checkbox" />
                  {selectedHostIds.size > 0 ? `${selectedHostIds.size} of ${pendingHosts.length} selected` : `Select all (${pendingHosts.length})`}
                </label>
                <div className="flex gap-2">
                  <Button size="sm" className="rounded-full bg-green-600 hover:bg-green-700 text-white text-xs disabled:opacity-50" disabled={selectedHostIds.size === 0} onClick={() => bulkActOnHosts("approve")} data-testid="bulk-approve-btn">
                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Bulk Approve {selectedHostIds.size > 0 && `(${selectedHostIds.size})`}
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-200 hover:bg-red-50 text-xs disabled:opacity-50" disabled={selectedHostIds.size === 0} onClick={() => bulkActOnHosts("reject")} data-testid="bulk-reject-btn">
                    <XCircle className="w-3.5 h-3.5 mr-1" /> Bulk Reject {selectedHostIds.size > 0 && `(${selectedHostIds.size})`}
                  </Button>
                </div>
              </div>
              {pendingHosts.map(h => {
              const m = h.partner_meta || {};
              const isSelected = selectedHostIds.has(h.id);
              return (
              <div key={h.id} className={`bg-white rounded-xl border ${isSelected ? "border-[#2C5F2D] ring-2 ring-[#2C5F2D]/20" : "border-[#E5E3DB]"} p-4 transition-all`} data-testid={`pending-host-${h.id}`}>
                <div className="flex items-start gap-4">
                  <input type="checkbox" className="w-4 h-4 mt-1 accent-[#2C5F2D] cursor-pointer" checked={isSelected} onChange={() => toggleHostSelection(h.id)} data-testid={`select-host-${h.id}`} />
                  <div className="w-12 h-12 rounded-full bg-[#2C5F2D]/10 flex items-center justify-center text-[#2C5F2D] font-bold font-['Outfit'] flex-shrink-0">
                    {h.name?.charAt(0).toUpperCase() || "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div>
                        <h4 className="font-['Outfit'] font-semibold text-[#1A231C] text-sm">{m.business_name || h.name} {m.business_name && <span className="text-xs text-[#8A998E] font-normal ml-2">· {h.name}</span>}</h4>
                        <p className="text-xs text-[#5C6B60] mt-0.5">{h.email} {h.phone && <>· {h.phone}</>}</p>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        <Button size="sm" className="rounded-full bg-green-600 hover:bg-green-700 text-white text-xs" onClick={() => actOnHost(h.id, "approve")} data-testid={`approve-host-${h.id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve</Button>
                        <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-200 hover:bg-red-50 text-xs" onClick={() => actOnHost(h.id, "reject")} data-testid={`reject-host-${h.id}`}><XCircle className="w-3.5 h-3.5 mr-1" /> Reject</Button>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <Badge className="bg-amber-100 text-amber-700 border-0 text-xs">Pending Review</Badge>
                      {m.property_type && <Badge className="bg-[#2C5F2D]/10 text-[#2C5F2D] border-0 text-xs capitalize">{m.property_type}</Badge>}
                      {m.agreement_accepted && <Badge className="bg-blue-100 text-blue-700 border-0 text-xs">Agreement signed</Badge>}
                      <span className="text-xs text-[#8A998E]">Applied {h.created_at?.slice(0, 10)}</span>
                    </div>
                    {(m.pan_number || m.gst_number || m.business_address) && (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1.5 mt-3 p-3 bg-[#F8F7F2] rounded-lg text-xs text-[#1A231C]">
                        {m.pan_number && <div><span className="text-[#5C6B60] font-medium">PAN:&nbsp;</span><span className="font-mono font-semibold text-[#1A231C]">{m.pan_number}</span></div>}
                        {m.gst_number && <div><span className="text-[#5C6B60] font-medium">GSTIN:&nbsp;</span><span className="font-mono font-semibold text-[#1A231C]">{m.gst_number}</span></div>}
                        {(m.city || m.state) && <div><span className="text-[#5C6B60] font-medium">Location:&nbsp;</span><span className="text-[#1A231C]">{[m.city, m.state].filter(Boolean).join(", ")}</span></div>}
                        {m.business_address && <div className="col-span-full"><span className="text-[#5C6B60] font-medium">Address:&nbsp;</span><span className="text-[#1A231C]">{m.business_address}</span></div>}
                        {m.bank_ifsc && <div><span className="text-[#5C6B60] font-medium">Bank IFSC:&nbsp;</span><span className="font-mono font-semibold text-[#1A231C]">{m.bank_ifsc}</span></div>}
                        {m.bank_account_number && <div><span className="text-[#5C6B60] font-medium">A/C:&nbsp;</span><span className="font-mono font-semibold text-[#1A231C]">****{m.bank_account_number.slice(-4)}</span></div>}
                        {m.bank_account_holder && <div className="col-span-full"><span className="text-[#5C6B60] font-medium">A/C Holder:&nbsp;</span><span className="font-semibold text-[#1A231C]">{m.bank_account_holder}</span></div>}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              );
            })}</>)}
          </div>
        </TabsContent>

        {/* Bookings Tab */}
        <TabsContent value="bookings">
          <div className="bg-white border border-[#E5E3DB] rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E5E3DB] bg-[#F3F2EE]">
                    <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-[#5C6B60]">ID</th>
                    <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-[#5C6B60]">Listing</th>
                    <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-[#5C6B60]">Customer</th>
                    <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-[#5C6B60]">Amount</th>
                    <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-[#5C6B60]">Commission</th>
                    <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-[#5C6B60]">Status</th>
                    <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-[#5C6B60]">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map(b => (
                    <tr key={b.id} className="border-b border-[#E5E3DB] hover:bg-[#FAF9F6]" data-testid={`admin-booking-${b.id}`}>
                      <td className="p-3 font-mono text-xs text-[#8A998E]">{b.id?.slice(0, 8).toUpperCase()}</td>
                      <td className="p-3">
                        <span className="text-[#1A231C] font-medium">{b.listing_title}</span>
                        <Badge className="ml-2 bg-[#F3F2EE] text-[#5C6B60] border-0 text-xs capitalize">{b.listing_type}</Badge>
                      </td>
                      <td className="p-3 text-[#5C6B60]">{b.customer_name}</td>
                      <td className="p-3 font-medium text-[#1A231C]">{b.currency} {b.amount?.toLocaleString()}</td>
                      <td className="p-3 text-[#E07A5F] font-medium">{b.currency} {b.commission_amount?.toLocaleString()}</td>
                      <td className="p-3"><Badge className={`border-0 text-xs ${statusColor(b.booking_status)}`}>{b.booking_status}</Badge></td>
                      <td className="p-3 text-[#8A998E] text-xs">{b.check_in}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {bookings.length === 0 && <div className="text-center py-12 text-[#8A998E]">No bookings yet</div>}
          </div>
        </TabsContent>

        {/* Listings Tab */}
        <TabsContent value="listings">
          <div className="space-y-3">
            <div className="bg-[#FFF7E6] border border-[#FDE68A] rounded-xl p-3 flex items-center justify-between gap-3" data-testid="listings-backfill-banner">
              <div className="text-xs text-[#78350F] flex-1">
                <p className="font-semibold">Listing type out of sync with the host's registered property type?</p>
                <p className="mt-0.5 text-[#92400E]">One-click backfill rewrites stay listings (Hotel/Homestay/Villa) to match each host's <code>partner_meta.property_type</code>. Per-listing override is also available inline.</p>
              </div>
              <Button size="sm" className="rounded-full bg-amber-600 hover:bg-amber-700 text-white text-xs flex-shrink-0" onClick={backfillListingTypes} data-testid="backfill-listing-types-btn">
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Backfill from partner meta
              </Button>
            </div>
            {listings.map(l => (
              <div key={l.id} className="bg-white rounded-xl border border-[#E5E3DB] p-4 flex items-center gap-4" data-testid={`admin-listing-${l.id}`}>
                <img src={l.images?.[0] || "https://images.unsplash.com/photo-1578683010236-d716f9a3f461?w=200"} alt="" className="w-14 h-14 rounded-lg object-cover" />
                <div className="flex-1 min-w-0">
                  <h4 className="font-['Outfit'] font-semibold text-[#1A231C] text-sm line-clamp-1">{l.title}</h4>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <select
                      value={l.type}
                      onChange={(e) => changeListingType(l.id, e.target.value, l.type)}
                      className="text-xs rounded-full bg-[#F3F2EE] border border-[#E5E3DB] px-2 py-0.5 text-[#1A231C] capitalize cursor-pointer hover:border-[#2C5F2D] focus:outline-none focus:ring-1 focus:ring-[#2C5F2D]"
                      data-testid={`listing-type-select-${l.id}`}
                      title="Change listing type"
                    >
                      {["hotel", "homestay", "villa", "cab", "bus", "package"].map(t => (
                        <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                      ))}
                    </select>
                    <Badge className={`border-0 text-xs ${statusColor(l.status)}`}>{l.status}</Badge>
                    <span className="text-xs text-[#8A998E]">by {l.host_name}</span>
                  </div>
                </div>
                <span className="text-sm font-bold text-[#2C5F2D] font-['Outfit']">{l.currency} {l.price?.toLocaleString()}</span>
                <Button
                  size="sm" variant="outline"
                  className={`rounded-full text-xs ${l.status === "active" ? "text-red-500" : "text-green-600"}`}
                  onClick={() => toggleListingStatus(l.id, l.status)}
                  data-testid={`toggle-listing-${l.id}`}
                >
                  {l.status === "active" ? <><XCircle className="w-3 h-3 mr-1" /> Deactivate</> : <><CheckCircle2 className="w-3 h-3 mr-1" /> Activate</>}
                </Button>
              </div>
            ))}
            {listings.length === 0 && <div className="text-center py-12 text-[#8A998E] bg-white rounded-xl border border-[#E5E3DB]">No listings</div>}
          </div>
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users">
          <div className="bg-white border border-[#E5E3DB] rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#E5E3DB] bg-[#F3F2EE]">
                    <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-[#5C6B60]">Name</th>
                    <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-[#5C6B60]">Email</th>
                    <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-[#5C6B60]">Role</th>
                    <th className="text-left p-3 text-xs font-semibold uppercase tracking-wider text-[#5C6B60]">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id} className="border-b border-[#E5E3DB] hover:bg-[#FAF9F6]" data-testid={`admin-user-${u.id}`}>
                      <td className="p-3 font-medium text-[#1A231C]">{u.name}</td>
                      <td className="p-3 text-[#5C6B60]">{u.email}</td>
                      <td className="p-3"><Badge className={`border-0 text-xs capitalize ${u.role === "admin" ? "bg-purple-100 text-purple-700" : u.role === "host" ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"}`}>{u.role}</Badge></td>
                      <td className="p-3 text-xs text-[#8A998E]">{u.created_at?.slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <div className="space-y-3">
            {notifications.map(n => (
              <div key={n.id} className="bg-white rounded-xl border border-[#E5E3DB] p-4 flex items-center gap-4" data-testid={`notification-${n.id}`}>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${n.type === "email" ? "bg-blue-50" : "bg-green-50"}`}>
                  <Bell className={`w-5 h-5 ${n.type === "email" ? "text-blue-500" : "text-green-500"}`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Badge className={`border-0 text-xs capitalize ${n.type === "email" ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"}`}>{n.type}</Badge>
                    <Badge className={`border-0 text-xs ${statusColor(n.status)}`}>{n.status}</Badge>
                  </div>
                  <p className="text-xs text-[#5C6B60] mt-1">{n.recipient} - {n.message?.slice(0, 60)}</p>
                </div>
                <span className="text-xs text-[#8A998E]">{n.created_at?.slice(0, 10)}</span>
              </div>
            ))}
            {notifications.length === 0 && <div className="text-center py-12 text-[#8A998E] bg-white rounded-xl border border-[#E5E3DB]">No notifications sent yet</div>}
          </div>
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings">
          <div className="max-w-md">
            <div className="bg-white border border-[#E5E3DB] rounded-xl p-6">
              <h3 className="font-['Outfit'] font-semibold text-[#1A231C] text-lg mb-4 flex items-center gap-2">
                <Settings className="w-5 h-5 text-[#2C5F2D]" /> Commission Rates
              </h3>
              <div className="space-y-4">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#5C6B60] flex items-center gap-1.5">
                    <Hotel className="w-3.5 h-3.5" /> Hotel Commission (%)
                  </Label>
                  <Input
                    type="number" step="0.5" min="0" max="50"
                    className="bg-white border-[#E5E3DB] rounded-xl mt-1"
                    value={commissionForm.hotel}
                    onChange={e => setCommissionForm(f => ({ ...f, hotel: parseFloat(e.target.value) || 0 }))}
                    data-testid="commission-hotel"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#5C6B60] flex items-center gap-1.5">
                    <Bus className="w-3.5 h-3.5" /> Bus Commission (%)
                  </Label>
                  <Input
                    type="number" step="0.5" min="0" max="50"
                    className="bg-white border-[#E5E3DB] rounded-xl mt-1"
                    value={commissionForm.bus}
                    onChange={e => setCommissionForm(f => ({ ...f, bus: parseFloat(e.target.value) || 0 }))}
                    data-testid="commission-bus"
                  />
                </div>
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#5C6B60] flex items-center gap-1.5">
                    <Car className="w-3.5 h-3.5" /> Cab Commission (%)
                  </Label>
                  <Input
                    type="number" step="0.5" min="0" max="50"
                    className="bg-white border-[#E5E3DB] rounded-xl mt-1"
                    value={commissionForm.cab}
                    onChange={e => setCommissionForm(f => ({ ...f, cab: parseFloat(e.target.value) || 0 }))}
                    data-testid="commission-cab"
                  />
                </div>
                <Button className="rounded-full bg-[#2C5F2D] hover:bg-[#214A22] text-white w-full" onClick={updateCommission} data-testid="save-commission-btn">
                  Save Commission Rates
                </Button>
              </div>

              <Separator className="bg-[#E5E3DB] my-6" />

              <h3 className="font-['Outfit'] font-semibold text-[#1A231C] text-lg mb-4">Integration Status</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#5C6B60]">Razorpay</span>
                  <Badge className={`border-0 text-xs ${settings?.razorpay_mock ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>
                    {settings?.razorpay_mock ? "MOCK MODE" : "LIVE"}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#5C6B60]">Resend (Email)</span>
                  <Badge className={`border-0 text-xs ${settings?.resend_mock ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>
                    {settings?.resend_mock ? "MOCK MODE" : "LIVE"}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#5C6B60]">Twilio (WhatsApp)</span>
                  <Badge className={`border-0 text-xs ${settings?.twilio_mock ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>
                    {settings?.twilio_mock ? "MOCK MODE" : "LIVE"}
                  </Badge>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* GST Reports Tab */}
        <TabsContent value="gst">
          <div className="space-y-4">
            <div className="bg-white rounded-xl border border-[#E5E3DB] p-5" data-testid="gst-reports-panel">
              <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
                <div>
                  <h3 className="font-['Outfit'] font-semibold text-[#1A231C] text-lg flex items-center gap-2"><Receipt className="w-5 h-5 text-[#2C5F2D]" /> GST Tax Invoices</h3>
                  <p className="text-xs text-[#8A998E] mt-1">GSTIN 21AADCU8136B1ZS · CIN U55101OD2025PTC048874 · SAC 998552 · Rate 18%</p>
                </div>
                <div className="flex gap-2 items-end">
                  <div>
                    <Label className="text-[11px] font-semibold uppercase tracking-[0.1em] text-[#5C6B60]">Filing Month</Label>
                    <Input type="month" className="bg-white border-[#E5E3DB] rounded-xl mt-1 h-9" value={gstMonth} onChange={e => { setGstMonth(e.target.value); loadGstInvoices(e.target.value); }} data-testid="gst-month-input" />
                  </div>
                  <Button className="rounded-full bg-[#2C5F2D] hover:bg-[#214A22] text-white h-9" onClick={() => downloadGstrCsv(gstMonth)} data-testid="gst-download-gstr1-btn">
                    <Download className="w-4 h-4 mr-1.5" /> Download GSTR-1 CSV
                  </Button>
                </div>
              </div>

              {gstData && gstData.summary && (
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5">
                  <div className="bg-[#F8FAFC] rounded-lg p-3 border border-[#E5E3DB]" data-testid="gst-summary-count"><div className="text-[10px] text-[#64748B] uppercase font-semibold">Invoices</div><div className="font-['Outfit'] font-bold text-[#0F172A] text-lg">{gstData.summary.count || 0}</div></div>
                  <div className="bg-[#F8FAFC] rounded-lg p-3 border border-[#E5E3DB]"><div className="text-[10px] text-[#64748B] uppercase font-semibold">Taxable</div><div className="font-['Outfit'] font-bold text-[#0F172A] text-lg">₹{(gstData.summary.total_taxable || 0).toLocaleString()}</div></div>
                  <div className="bg-[#F8FAFC] rounded-lg p-3 border border-[#E5E3DB]"><div className="text-[10px] text-[#64748B] uppercase font-semibold">CGST</div><div className="font-['Outfit'] font-bold text-[#0F172A] text-lg">₹{(gstData.summary.total_cgst || 0).toLocaleString()}</div></div>
                  <div className="bg-[#F8FAFC] rounded-lg p-3 border border-[#E5E3DB]"><div className="text-[10px] text-[#64748B] uppercase font-semibold">SGST</div><div className="font-['Outfit'] font-bold text-[#0F172A] text-lg">₹{(gstData.summary.total_sgst || 0).toLocaleString()}</div></div>
                  <div className="bg-[#F8FAFC] rounded-lg p-3 border border-[#E5E3DB]"><div className="text-[10px] text-[#64748B] uppercase font-semibold">IGST</div><div className="font-['Outfit'] font-bold text-[#0F172A] text-lg">₹{(gstData.summary.total_igst || 0).toLocaleString()}</div></div>
                  <div className="bg-[#0F172A] rounded-lg p-3 border border-[#0F172A]" data-testid="gst-summary-grand"><div className="text-[10px] text-white/60 uppercase font-semibold">Grand Total</div><div className="font-['Outfit'] font-bold text-white text-lg">₹{(gstData.summary.grand_total || 0).toLocaleString()}</div></div>
                </div>
              )}

              {gstLoading ? (
                <div className="text-center py-10 text-[#8A998E]">Loading invoices…</div>
              ) : gstData && gstData.items && gstData.items.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="gst-invoices-table">
                    <thead>
                      <tr className="text-left text-[11px] uppercase tracking-wider text-[#64748B] border-b border-[#E5E3DB]">
                        <th className="py-2 pr-3">Invoice #</th>
                        <th className="py-2 pr-3">Date</th>
                        <th className="py-2 pr-3">Customer</th>
                        <th className="py-2 pr-3">GSTIN</th>
                        <th className="py-2 pr-3">POS</th>
                        <th className="py-2 pr-3 text-right">Taxable</th>
                        <th className="py-2 pr-3 text-right">Tax</th>
                        <th className="py-2 pr-3 text-right">Total</th>
                        <th className="py-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {gstData.items.map(inv => (
                        <tr key={inv.invoice_number} className="border-b border-[#F1F5F9]" data-testid={`gst-invoice-row-${inv.invoice_number}`}>
                          <td className="py-2 pr-3 font-mono text-xs text-[#0F172A]">{inv.invoice_number}</td>
                          <td className="py-2 pr-3 text-[#475569]">{(inv.issued_at || "").slice(0, 10)}</td>
                          <td className="py-2 pr-3 text-[#0F172A]">{inv.customer_name}</td>
                          <td className="py-2 pr-3 font-mono text-xs text-[#475569]">{inv.customer_gstin || <span className="text-[#94A3B8]">—</span>}</td>
                          <td className="py-2 pr-3">{inv.place_of_supply_code}</td>
                          <td className="py-2 pr-3 text-right">₹{(inv.taxable_value || 0).toLocaleString()}</td>
                          <td className="py-2 pr-3 text-right">₹{(inv.total_tax || 0).toLocaleString()}</td>
                          <td className="py-2 pr-3 text-right font-semibold">₹{(inv.grand_total || 0).toLocaleString()}</td>
                          <td className="py-2">
                            <Button size="sm" variant="outline" className="rounded-full h-7 text-xs" onClick={() => downloadAdminInvoice(inv.booking_id, inv.invoice_number)} data-testid={`gst-dl-inv-${inv.invoice_number}`}>
                              <Download className="w-3 h-3 mr-1" /> PDF
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-12 text-[#8A998E] bg-[#F8FAFC] rounded-xl border border-dashed border-[#E5E3DB]">
                  <Receipt className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p className="font-medium">No invoices for {gstMonth}</p>
                  <p className="text-xs mt-1">Invoices are auto-generated when bookings are confirmed.</p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Blog Tab */}
        <TabsContent value="blog">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-['Outfit'] font-semibold text-[#1A231C]">Blog Posts</h3>
            <Button className="rounded-full bg-[#2C5F2D] hover:bg-[#214A22] text-white" onClick={() => { setEditingBlog(null); setBlogForm({ title: "", content: "", excerpt: "", image: "", published: true }); setShowBlogForm(true); }} data-testid="add-blog-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New Post
            </Button>
          </div>
          <div className="space-y-3">
            {blogs.map(b => (
              <div key={b.id} className="bg-white rounded-xl border border-[#E5E3DB] p-4 flex items-center gap-4" data-testid={`blog-${b.id}`}>
                {b.image && <img src={b.image} alt="" className="w-16 h-16 rounded-lg object-cover" />}
                <div className="flex-1 min-w-0">
                  <h4 className="font-['Outfit'] font-semibold text-[#1A231C] text-sm">{b.title}</h4>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge className={`border-0 text-xs ${b.published ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>{b.published ? "Published" : "Draft"}</Badge>
                    <span className="text-xs text-[#8A998E]">{b.created_at?.slice(0, 10)}</span>
                    <span className="text-xs text-[#8A998E]">/{b.slug}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="rounded-full h-8 w-8 p-0" onClick={() => { setEditingBlog(b); setBlogForm({ title: b.title, content: b.content, excerpt: b.excerpt, image: b.image, published: b.published }); setShowBlogForm(true); }} data-testid={`edit-blog-${b.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button size="sm" variant="outline" className="rounded-full h-8 w-8 p-0 text-red-500" onClick={() => deleteBlog(b.id)} data-testid={`delete-blog-${b.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            ))}
            {blogs.length === 0 && <div className="text-center py-12 text-[#8A998E] bg-white rounded-xl border border-[#E5E3DB]"><FileText className="w-10 h-10 mx-auto mb-3" /><p>No blog posts yet</p></div>}
          </div>
        </TabsContent>

        {/* Destinations Tab */}
        <TabsContent value="destinations">
          <DestinationsTab />
        </TabsContent>

        {/* Reviews Moderation Tab */}
        <TabsContent value="reviews">
          <ReviewsModerationTab />
        </TabsContent>

        {/* Corporate B2B Tab */}
        <TabsContent value="companies">
          <CompaniesTab />
        </TabsContent>

        {/* Affiliates Tab */}
        <TabsContent value="affiliates">
          <AffiliatesTab />
        </TabsContent>

        <TabsContent value="pending-listings">
          <PendingListingsTab />
        </TabsContent>

        <TabsContent value="migration">
          <MigrationStatusTab />
        </TabsContent>

        <TabsContent value="redis">
          <RedisStatsTab />
        </TabsContent>

        <TabsContent value="doc-verify">
          <DocVerificationTab />
        </TabsContent>

        <TabsContent value="deletions">
          <DeletionsTab />
        </TabsContent>

        <TabsContent value="profile">
          <AdminProfileTab />
        </TabsContent>
      </Tabs>

      {/* Blog Form Dialog */}
      <Dialog open={showBlogForm} onOpenChange={setShowBlogForm}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="blog-form-dialog">
          <DialogHeader><DialogTitle className="font-['Outfit']">{editingBlog ? "Edit Blog Post" : "New Blog Post"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#5C6B60]">Title</Label><Input className="bg-white border-[#E5E3DB] rounded-xl mt-1" value={blogForm.title} onChange={e => setBlogForm(f => ({ ...f, title: e.target.value }))} placeholder="Blog post title" data-testid="blog-title-input" /></div>
            <div><Label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#5C6B60]">Excerpt</Label><Input className="bg-white border-[#E5E3DB] rounded-xl mt-1" value={blogForm.excerpt} onChange={e => setBlogForm(f => ({ ...f, excerpt: e.target.value }))} placeholder="Short summary..." data-testid="blog-excerpt-input" /></div>
            <div><Label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#5C6B60]">Image URL</Label><Input className="bg-white border-[#E5E3DB] rounded-xl mt-1" value={blogForm.image} onChange={e => setBlogForm(f => ({ ...f, image: e.target.value }))} placeholder="https://..." data-testid="blog-image-input" /></div>
            <div><Label className="text-xs font-semibold uppercase tracking-[0.1em] text-[#5C6B60]">Content</Label><Textarea className="bg-white border-[#E5E3DB] rounded-xl mt-1 min-h-[200px]" value={blogForm.content} onChange={e => setBlogForm(f => ({ ...f, content: e.target.value }))} placeholder="Write your blog post..." data-testid="blog-content-input" /></div>
            <div className="flex items-center gap-2"><input type="checkbox" checked={blogForm.published} onChange={e => setBlogForm(f => ({ ...f, published: e.target.checked }))} data-testid="blog-published-check" /><Label className="text-sm text-[#5C6B60]">Publish immediately</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setShowBlogForm(false)}>Cancel</Button>
            <Button className="rounded-full bg-[#2C5F2D] hover:bg-[#214A22] text-white" onClick={handleBlogSubmit} data-testid="blog-submit-btn">{editingBlog ? "Update" : "Publish"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
